import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../api';
import logoFull from '../../assets/brand/logo-full-trimmed.png';

const CustomerAuthPage = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const initialTab = params.get('tab') === 'register' ? 'register' : 'login';
    const [mode, setMode] = useState(initialTab);
    const activated = params.get('activated') === 'true';

    useEffect(() => { setMode(initialTab); }, [initialTab, location.search]);

    const [lastName, setLastName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);
    const [focusedField, setFocusedField] = useState(null);
    const navigate = useNavigate();

    // OTPログイン
    const [loginStep, setLoginStep] = useState(1); // 1: メール入力, 2: コード入力
    const [loginCode, setLoginCode] = useState('');
    const [sending, setSending] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown(c => c - 1), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const sendOtp = async (targetEmail) => {
        setSending(true);
        setError(null);
        try {
            await api.post('otp/send', { email: targetEmail, user_type: 'customer' });
            setLoginStep(2);
            setCooldown(60);
        } catch (err) {
            setError(err.response?.data?.detail || 'コードの送信に失敗しました。');
        } finally {
            setSending(false);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        await sendOtp(email);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            const res = await api.post('otp/verify', { email, code: loginCode, user_type: 'customer' });
            localStorage.setItem('access_token', res.data.access);
            localStorage.setItem('refresh_token', res.data.refresh);
            localStorage.setItem('user_id', res.data.user_id);
            if (res.data.customer_id) {
                localStorage.setItem('customer_id', res.data.customer_id);
                navigate('/customer/dashboard');
            } else {
                navigate('/customers');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'コードが正しくありません。');
            setLoginCode('');
        }
    };

    const handleResendOtp = async () => {
        setLoginCode('');
        await sendOtp(email);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            await api.post('accounts/register/', { email, last_name: lastName, first_name: firstName });
            setMode('login');
            await sendOtp(email); // 登録完了後にOTPを自動送信してStep2へ
        } catch (err) {
            const msg = err.response?.data?.errors?.email?.[0]
                || err.response?.data?.message
                || '登録に失敗しました。';
            setError(msg);
        }
    };

    const inp = (field) => ({
        width: '100%', padding: '10px 14px',
        border: `1px solid ${focusedField === field ? '#005c53' : '#e0e4ea'}`,
        borderRadius: 8, fontSize: '0.9rem', color: '#1a1a2e',
        outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
        background: '#fff',
    });

    const LBL = { display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 6 };

    return (
        <div style={{
            minHeight: '80vh',
            background: 'linear-gradient(135deg, rgba(0,169,157,0.1) 0%, #fff 45%, rgba(254,243,199,0.35) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
            <style>{`
                @keyframes loginFadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes formSlideIn  { from { opacity:0; transform:translateX(-16px); } to { opacity:1; transform:translateX(0); } }
                @keyframes formSlideInR { from { opacity:0; transform:translateX(16px);  } to { opacity:1; transform:translateX(0); } }
                .login-card    { animation: loginFadeUp 0.5s ease both; }
                .form-login    { animation: formSlideIn  0.3s ease both; }
                .form-register { animation: formSlideInR 0.3s ease both; }
                .cust-btn {
                    width:100%; padding:13px;
                    background: linear-gradient(135deg, #00a99d, #005c53);
                    color:#fff; border:none; border-radius:8px;
                    font-weight:600; font-size:0.95rem; cursor:pointer;
                    transition: opacity 0.2s, transform 0.15s;
                    box-shadow: 0 4px 14px rgba(0,92,83,0.25);
                }
                .cust-btn:hover  { opacity:0.9; transform:translateY(-1px); }
                .cust-btn:active { transform:translateY(0); }
                @media (max-width: 640px) { .qr-mobile-box { display: none !important; } }
            `}</style>

            <div className="login-card" style={{
                width: '100%', maxWidth: 400, background: '#fff',
                borderRadius: 16, padding: '36px 32px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            }}>
                {/* ロゴ */}
                <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: 4, textDecoration: 'none' }}>
                    <img src={logoFull} alt="のりいれ三丁目" style={{ height: 40, display: 'block' }} />
                </Link>
                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', marginBottom: 28 }}>顧客ログイン</p>

                {/* スマホでログインQR（PC画面でのみ表示） */}
                <div className="qr-mobile-box" style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#f5f6f8', borderRadius: 10, padding: '14px 16px', marginBottom: 24 }}>
                    <div style={{ background: '#fff', padding: 6, borderRadius: 6, flexShrink: 0 }}>
                        <QRCodeSVG value={typeof window !== 'undefined' ? window.location.href : ''} size={64} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6 }}>
                        スマホのカメラでQRコードを読み取ると、このログイン画面をスマホで開けます
                    </p>
                </div>

                {/* タブ */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e0e4ea', marginBottom: 28 }}>
                    {[{ key: 'login', label: 'ログイン' }, { key: 'register', label: '新規登録' }].map(({ key, label }) => (
                        <button key={key} onClick={() => { setMode(key); setError(null); setLoginStep(1); setLoginCode(''); }} style={{
                            flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '0.9rem', fontWeight: mode === key ? 600 : 400,
                            color: mode === key ? '#005c53' : '#6b7280',
                            borderBottom: mode === key ? '2px solid #005c53' : '2px solid transparent',
                            marginBottom: -1, transition: 'all 0.2s',
                        }}>
                            {label}
                        </button>
                    ))}
                </div>

                {activated && (
                    <p style={{ color: '#005c53', fontSize: '0.85rem', marginBottom: 16 }}>
                        アカウントが有効化されました。ログインしてください。
                    </p>
                )}

                {/* ログインフォーム（OTP） */}
                {mode === 'login' && (
                    <div className="form-login">
                        {loginStep === 1 ? (
                            <form onSubmit={handleSendOtp}>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={LBL}>メールアドレス</label>
                                    <input type="email" placeholder="example@email.com" value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                                        style={inp('email')} required />
                                </div>
                                {error && <p style={{ color: error.includes('完了') ? '#005c53' : '#e53935', fontSize: '0.85rem', marginBottom: 12 }}>{error}</p>}
                                <button type="submit" className="cust-btn" disabled={sending}>
                                    {sending ? '送信中...' : '認証コードを送信する'}
                                </button>
                            </form>
                        ) : (
                            <div>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 20, lineHeight: 1.7 }}>
                                    <strong style={{ color: '#1a1a2e' }}>{email}</strong><br />
                                    に6桁のコードを送信しました。
                                </p>
                                <form onSubmit={handleVerifyOtp}>
                                    <div style={{ marginBottom: 20 }}>
                                        <label style={LBL}>認証コード</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={loginCode}
                                            onChange={e => setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            onFocus={() => setFocusedField('code')} onBlur={() => setFocusedField(null)}
                                            placeholder="000000"
                                            autoFocus
                                            style={{
                                                ...inp('code'),
                                                fontSize: '2rem',
                                                letterSpacing: '0.5em',
                                                textAlign: 'center',
                                                fontFamily: 'monospace',
                                                padding: '16px 14px',
                                            }}
                                        />
                                    </div>
                                    {error && <p style={{ color: '#e53935', fontSize: '0.85rem', marginBottom: 12 }}>{error}</p>}
                                    <button type="submit" className="cust-btn" disabled={loginCode.length < 6}>
                                        ログイン
                                    </button>
                                </form>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, fontSize: '0.83rem' }}>
                                    {cooldown > 0 ? (
                                        <span style={{ color: '#9ca3af' }}>{cooldown}秒後に再送できます</span>
                                    ) : (
                                        <button onClick={handleResendOtp} disabled={sending} style={{ background: 'none', border: 'none', color: '#005c53', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', padding: 0 }}>
                                            コードを再送する
                                        </button>
                                    )}
                                    <span style={{ color: '#e0e4ea' }}>|</span>
                                    <button onClick={() => { setLoginStep(1); setLoginCode(''); setError(null); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.83rem', padding: 0 }}>
                                        メールを変更する
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 登録フォーム */}
                {mode === 'register' && (
                    <form className="form-register" onSubmit={handleRegister}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                            <div>
                                <label style={LBL}>姓</label>
                                <input type="text" placeholder="山田" value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    onFocus={() => setFocusedField('lastName')} onBlur={() => setFocusedField(null)}
                                    style={inp('lastName')} />
                            </div>
                            <div>
                                <label style={LBL}>名</label>
                                <input type="text" placeholder="太郎" value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    onFocus={() => setFocusedField('firstName')} onBlur={() => setFocusedField(null)}
                                    style={inp('firstName')} />
                            </div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={LBL}>メールアドレス</label>
                            <input type="email" placeholder="example@email.com" value={email}
                                onChange={e => setEmail(e.target.value)}
                                onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                                style={inp('email')} required />
                        </div>
                        {error && <p style={{ color: error.includes('完了') ? '#005c53' : '#e53935', fontSize: '0.85rem', marginBottom: 12 }}>{error}</p>}
                        <button type="submit" className="cust-btn">新規登録</button>
                    </form>
                )}

                {/* 業者へのリンク */}
                <div style={{ borderTop: '1px solid #e0e4ea', marginTop: 24, paddingTop: 18, textAlign: 'center', fontSize: '0.85rem', color: '#6b7280' }}>
                    業者の方はこちら{' '}
                    <Link to="/contractor/login" style={{ color: '#005c53', fontWeight: 600, textDecoration: 'none' }}>
                        業者ログイン
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CustomerAuthPage;
